// ============================================
// FILE: frontend/src/hooks/useMatching.js
// PURPOSE: Custom hook for matching operations
// ============================================

import { useState, useCallback } from 'react';
import { matchingAPI } from '../api/matching.api.js';

console.log('[useMatching] Initializing useMatching hook');

// ============================================
// MAIN HOOK
// ============================================

export const useMatching = () => {
  console.log('[useMatching] Hook created');

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const [matches, setMatches] = useState([]);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [runningMatching, setRunningMatching] = useState(false);
  
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 0
  });

  // ========================================
  // RUN MATCHING ALGORITHM
  // ========================================

  const runMatching = useCallback(async () => {
    console.log('[useMatching] 🔧 Triggering matching algorithm...');
    setRunningMatching(true);
    setError(null);

    try {
      const response = await matchingAPI.runMatching();
      console.log('[useMatching] ✅ Matching complete:', {
        totalAttempts: response.stats?.totalAttempts,
        matchesCreated: response.stats?.matchesCreated
      });
      return response;
    } catch (err) {
      console.error('[useMatching] ❌ Matching failed:', err);
      const errorMsg = err.response?.data?.message || 'Failed to run matching algorithm';
      setError(errorMsg);
      throw err;
    } finally {
      setRunningMatching(false);
    }
  }, []);

  // ========================================
  // FETCH ALL MATCHES
  // ========================================

  const fetchMatches = useCallback(async (page = 1, limit = 20, status = 'pending_review', sortBy = 'score') => {
    console.log('[useMatching] 📋 Fetching matches...', { page, limit, status, sortBy });
    setLoading(true);
    setError(null);

    try {
      const response = await matchingAPI.getMatches(page, limit, status, sortBy);
      console.log('[useMatching] ✅ Matches fetched:', {
        count: response.matches?.length,
        total: response.pagination?.total
      });

      setMatches(response.matches || []);
      setPagination(response.pagination || {});
      return response;
    } catch (err) {
      console.error('[useMatching] ❌ Failed to fetch matches:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load matches';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // FETCH SINGLE MATCH DETAILS
  // ========================================

  const fetchMatchDetails = useCallback(async (matchId) => {
    console.log('[useMatching] 🔍 Fetching match details:', matchId);
    setLoading(true);
    setError(null);

    try {
      const response = await matchingAPI.getMatchDetails(matchId);
      console.log('[useMatching] ✅ Match details retrieved:', {
        score: response.match?.matchScore,
        landownerEmail: response.match?.landowner?.user?.email
      });

      setCurrentMatch(response.match);
      return response;
    } catch (err) {
      console.error('[useMatching] ❌ Failed to fetch match details:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load match details';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // REVIEW MATCH (APPROVE/REJECT)
  // ========================================

  const reviewMatch = useCallback(async (matchId, action, adminNotes = '') => {
    console.log('[useMatching] 📝 Reviewing match:', { matchId, action });
    setReviewingId(matchId);
    setError(null);

    try {
      const response = await matchingAPI.reviewMatch(matchId, action, adminNotes);
      console.log('[useMatching] ✅ Match reviewed:', {
        newStatus: response.match?.status,
        action
      });

      // Update local state
      setMatches(prevMatches =>
        prevMatches.map(m => (m.id === matchId ? response.match : m))
      );

      if (currentMatch?.id === matchId) {
        setCurrentMatch(response.match);
      }

      return response;
    } catch (err) {
      console.error('[useMatching] ❌ Failed to review match:', err);
      const errorMsg = err.response?.data?.message || 'Failed to review match';
      setError(errorMsg);
      throw err;
    } finally {
      setReviewingId(null);
    }
  }, [currentMatch]);

  // ========================================
  // FETCH MATCHING ATTEMPTS (ANALYTICS)
  // ========================================

  const fetchAttempts = useCallback(async (page = 1, limit = 50) => {
    console.log('[useMatching] 📊 Fetching matching attempts...', { page, limit });
    setLoading(true);
    setError(null);

    try {
      const response = await matchingAPI.getMatchingAttempts(page, limit);
      console.log('[useMatching] ✅ Attempts retrieved:', {
        count: response.attempts?.length,
        statistics: response.statistics
      });

      setAttempts(response.attempts || []);
      setStatistics(response.statistics || {});
      setPagination(response.pagination || {});
      return response;
    } catch (err) {
      console.error('[useMatching] ❌ Failed to fetch attempts:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load attempts';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // RESET STATE
  // ========================================

  const resetState = useCallback(() => {
    console.log('[useMatching] 🔄 Resetting state...');
    setMatches([]);
    setCurrentMatch(null);
    setAttempts([]);
    setStatistics(null);
    setError(null);
    setLoading(false);
    setReviewingId(null);
    setRunningMatching(false);
  }, []);

  // ========================================
  // RETURN HOOK API
  // ========================================

  return {
    // State
    matches,
    currentMatch,
    attempts,
    statistics,
    loading,
    reviewingId,
    runningMatching,
    error,
    pagination,

    // Methods
    runMatching,
    fetchMatches,
    fetchMatchDetails,
    reviewMatch,
    fetchAttempts,
    resetState
  };
};

export default useMatching;